import React from 'react';
import { useLanguage } from '../context/LanguageContext';

// High quality zone images
const ZONE_IMAGES = {
  tulum: "https://customer-assets.emergentagent.com/job_tulum-investment-lab/artifacts/kb7t8yp7_1706.jpg",
  playaDelCarmen: "https://customer-assets.emergentagent.com/job_tulum-investment-lab/artifacts/tmru9w93_freepik_genera-la-imagen-en-916-composicion-vertical-completa-bien-encuadrada-con-el-sujeto-principal-centrado-y-claramente-visible-fondo-detallado-pero-no-distractor-iluminacion-equilibrada-y-ni_0001.jpeg",
  puertoMorelos: "https://customer-assets.emergentagent.com/job_tulum-investment-lab/artifacts/h615nw8b_freepik_0001%20%281%29.jpeg",
  cancun: "https://customer-assets.emergentagent.com/job_tulum-investment-lab/artifacts/rnei00iz_CANC%C3%9AN%20%28M%C3%89XICO%29%20%281%29.jfif"
};

export const Zones = () => {
  const { t, language } = useLanguage();

  const zones = [
    {
      id: 'tulum',
      image: ZONE_IMAGES.tulum,
      name: t('zones.tulum.name'),
      description: language === 'es' 
        ? 'Epicentro del diseño arquitectónico y la sostenibilidad. Aldea Zama, La Veleta y Región 15 lideran en demanda y plusvalía. Proyectos eco-friendly con alta rentabilidad Airbnb.'
        : 'Epicenter of architectural design and sustainability. Aldea Zama, La Veleta and Region 15 lead in demand and appreciation. Eco-friendly projects with high Airbnb profitability.',
      ticket: language === 'es' 
        ? 'Ticket promedio: $145,000 – $500,000 USD' 
        : 'Average ticket: $145,000 – $500,000 USD',
      highlights: language === 'es'
        ? ['Plusvalía 12-15% anual', 'Rendimiento 10-13%', 'Ocupación 80%+']
        : ['12-15% annual appreciation', '10-13% yield', '80%+ occupancy']
    },
    {
      id: 'playaDelCarmen',
      image: ZONE_IMAGES.playaDelCarmen,
      name: t('zones.playaDelCarmen.name'),
      description: language === 'es'
        ? 'Zonas Colosio y Ejidal en plena transformación. Alta demanda de nómadas digitales y turistas. Infraestructura consolidada y conectividad total con Cancún.'
        : 'Colosio and Ejidal areas in full transformation. High demand from digital nomads and tourists. Consolidated infrastructure and total connectivity with Cancun.',
      ticket: language === 'es'
        ? 'Ticket promedio: $120,000 – $300,000 USD'
        : 'Average ticket: $120,000 – $300,000 USD',
      highlights: language === 'es'
        ? ['Plusvalía 10-12% anual', 'Rendimiento 9-11%', 'A 45 min del aeropuerto']
        : ['10-12% annual appreciation', '9-11% yield', '45 min from airport']
    },
    {
      id: 'puertoMorelos',
      image: ZONE_IMAGES.puertoMorelos,
      name: t('zones.puertoMorelos.name'),
      description: language === 'es'
        ? 'El diamante oculto de 2026. Mayor tasa de absorción de la región, nuevos accesos a playa y proyectos frente al mar. Precios de entrada accesibles con alto potencial.'
        : 'The hidden gem of 2026. Highest absorption rate in the region, new beach access and beachfront projects. Accessible entry prices with high potential.',
      ticket: language === 'es'
        ? 'Ticket promedio: $90,000 – $200,000 USD'
        : 'Average ticket: $90,000 – $200,000 USD',
      highlights: language === 'es'
        ? ['Plusvalía 15%+ proyectada', 'Rendimiento 8-10%', 'Frente al mar desde $167k']
        : ['15%+ projected appreciation', '8-10% yield', 'Beachfront from $167k']
    },
    {
      id: 'cancun',
      image: ZONE_IMAGES.cancun,
      name: 'Cancún',
      description: language === 'es'
        ? 'El destino turístico #1 de Latinoamérica con más de 30 millones de visitantes anuales. Zona Hotelera, Puerto Cancún y Cancún Centro lideran la inversión. Aeropuerto internacional con conexión directa a 80+ ciudades.'
        : 'Latin America\'s #1 tourist destination with over 30 million annual visitors. Hotel Zone, Puerto Cancún and Downtown lead investment. International airport with direct connections to 80+ cities.',
      ticket: language === 'es'
        ? 'Ticket promedio: $150,000 – $450,000 USD'
        : 'Average ticket: $150,000 – $450,000 USD',
      highlights: language === 'es'
        ? ['Plusvalía 8-12% anual', 'Rendimiento 8-13%', 'Ocupación 75%+']
        : ['8-12% annual appreciation', '8-13% yield', '75%+ occupancy']
    }
  ];

  return (
    <section
      id="zones"
      data-testid="zones-section"
      className="bg-[#FAFAF7] py-16 md:py-[60px]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <h2
          className="font-playfair text-3xl sm:text-4xl lg:text-5xl text-[#0A2235] text-center mb-16 tracking-tight"
          data-testid="zones-section-title"
        >
          {t('zones.sectionTitle')}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {zones.map((zone, index) => (
            <div
              key={zone.id}
              className="relative h-[550px] md:h-[620px] overflow-hidden group rounded-[20px]"
              data-testid={`zone-${zone.id}`}
            >
              {/* Background Image */}
              <img
                src={zone.image}
                alt={zone.name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A2235]/95 via-[#0A2235]/40 to-transparent" />

              {/* Content */}
              <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-10">
                <h3 className="font-playfair text-2xl md:text-3xl text-[#FAFAF7] mb-4 tracking-wide">
                  {zone.name}
                </h3>
                <p className="font-inter text-[#FAFAF7]/80 text-sm md:text-base leading-[1.7] mb-4 font-light">
                  {zone.description}
                </p>
                
                {/* Highlights */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {zone.highlights.map((highlight) => (
                    <span 
                      key={highlight}
                      className="bg-[#2BBFBF]/20 text-[#2BBFBF] text-xs px-3 py-1 font-inter rounded-full"
                    >
                      {highlight}
                    </span>
                  ))}
                </div>

                <p className="font-inter text-[#2BBFBF] text-sm font-medium">
                  {zone.ticket}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
